import React, { useState, useRef, useLayoutEffect, useEffect } from "react";
import SEED from "../data/stack.json";

/* ─────────────────────────────────────────────────────────────────────────
   DATA MODEL: every block is the same shape, at every depth.
     { id, name, sub, status, x, y, w, info: {label: text | [list]},
       children: [blocks], links: [{from, to, fromSide, toSide, label}] }
   - x, y, w place a block on its parent's map (pixels).
   - links connect two children. Sides are "n", "e", "s", "w".
   - status: "s" stable, "w" in progress, "p" planned.
   A block with children shows a small preview of its own map inside it.
   The map data lives in src/data/stack.json. Open the page with ?edit=1 to
   rearrange it, then "Download JSON" and replace that file to publish.
   All content is placeholder.
   ───────────────────────────────────────────────────────────────────────── */

const STATUS = { s: "Stable", w: "In progress", p: "Planned" };
const SIDES = { n: "North", e: "East", s: "South", w: "West" };
const NORMAL = { n: [0, -1], s: [0, 1], w: [-1, 0], e: [1, 0] };
const CARD_PAD = 35; /* card padding (16 each side) plus border */
const ZMIN = 0.2, ZMAX = 4;
const clampZ = (z) => Math.max(ZMIN, Math.min(ZMAX, z));

/* Flatten every descendant block to { path, label } for the cross-arrow editor. */
function flattenBlocks(root) {
  const out = [];
  const walk = (node, path, label) => {
    (node.children || []).forEach((c) => {
      const p = path ? path + "/" + c.id : c.id;
      const l = label ? label + " › " + c.name : c.name;
      out.push({ path: p, label: l });
      walk(c, p, l);
    });
  };
  walk(root, "", "");
  return out;
}
/* Resolve a "id/id" path to its id chain, or null if it no longer exists. */
function resolvePath(root, pathStr) {
  if (!pathStr) return null;
  const ids = pathStr.split("/");
  let node = root;
  for (const id of ids) {
    const c = (node.children || []).find((k) => k.id === id);
    if (!c) return null;
    node = c;
  }
  return { ids, node };
}
/* One cubic between two anchors; each has x,y and an outward normal nx,ny.
   ox/oy bow the curve (offsetting both control points) so parallel or
   reciprocal links between the same pair don't land on top of each other. */
function bez(a, b, ox = 0, oy = 0) {
  const d = Math.min(160, Math.max(30, Math.hypot(b.x - a.x, b.y - a.y) * 0.45));
  const c1 = { x: a.x + a.nx * d + ox, y: a.y + a.ny * d + oy };
  const c2 = { x: b.x + b.nx * d + ox, y: b.y + b.ny * d + oy };
  return {
    d: `M${a.x} ${a.y}C${c1.x} ${c1.y} ${c2.x} ${c2.y} ${b.x} ${b.y}`,
    lx: (a.x + 3 * c1.x + 3 * c2.x + b.x) / 8,
    ly: (a.y + 3 * c1.y + 3 * c2.y + b.y) / 8,
  };
}

const CSS = `
.sm{--bg:#0F140F;--ink:#E7ECE5;--soft:#9DB0A2;--card:#1A211B;--pass:#F2792E;--turf:#54B07E;--tint:#273229;
  font-family:"Avenir Next","Segoe UI",system-ui,sans-serif;color:var(--ink);background:var(--bg);
  min-height:100vh;display:flex;flex-direction:column;
  padding:28px;line-height:1.45;box-sizing:border-box}
.sm *{box-sizing:border-box}
:where(.sm) button{font:inherit;color:inherit;cursor:pointer}
:where(.sm) :is(h2,p,ul,dl,dd){margin:0;padding:0}
:where(.sm) ul{list-style:none}
:where(.sm) :is(input,select,textarea){font:inherit;color:inherit;background:var(--card);border:1px solid var(--soft);border-radius:4px;padding:5px 7px;min-width:0}
:where(.sm) dt{font-size:13px;color:var(--soft);margin-bottom:2px}
:where(.sm) dd{font-size:15px}
.sm :is(button,input,select,textarea):focus-visible{outline:3px solid var(--pass);outline-offset:2px}
.sm-top{display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap;margin-bottom:18px}
.sm-crumbs{display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;font-size:15px}
.sm-link{background:none;border:0;padding:0;text-decoration:underline;text-underline-offset:3px}
.sm-here{font-weight:600}
.sm-bar{display:flex;gap:8px;flex-wrap:wrap}
.sm-btn{background:var(--card);border:1.5px solid var(--ink);border-radius:4px;padding:6px 12px;font-size:14px}
.sm-btn.on{background:var(--ink);color:var(--bg)}
.sm-btn.quiet{border-color:transparent;background:none;text-decoration:underline;text-underline-offset:3px;padding:6px 4px}
.sm-h{font-size:30px;font-weight:650;letter-spacing:-0.02em;line-height:1.15}
.sm-sub{font-size:14px;color:var(--soft)}
.sm-status{display:inline-flex;align-items:center;gap:6px;font-size:13px;color:var(--soft)}
.sm-facts{display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:16px 28px;margin:18px 0 24px}
.sm-facts li{padding-left:14px;position:relative}
.sm-facts li::before{content:"";position:absolute;left:0;top:0.65em;width:6px;height:2px;background:var(--ink)}
.sm-stage{position:relative;overflow:hidden}
.sm-stage.pan{cursor:grab}
.sm-stage.pan:active{cursor:grabbing}
.sm-stage.edit{overflow:hidden;cursor:grab;border:1.5px dashed var(--soft);border-radius:6px;
  background-image:radial-gradient(var(--tint) 1.5px,transparent 1.5px);background-size:20px 20px}
.sm-stage.edit:active{cursor:grabbing}
.sm-origin line{stroke:var(--pass);stroke-width:2.5}
.sm-origin circle{fill:var(--pass)}
.sm-origin text{font-size:12px;font-weight:600;fill:var(--pass);paint-order:stroke;stroke:var(--bg);stroke-width:5px;stroke-linejoin:round}
.sm-canvas{position:relative;transform-origin:0 0}
.sm-block{position:absolute;background:var(--card);border:1.5px solid var(--ink);border-radius:6px;padding:16px;
  cursor:pointer;display:flex;flex-direction:column;gap:12px}
.sm-block:hover{box-shadow:0 0 0 4px var(--tint)}
.sm-block.edit{cursor:grab;touch-action:none;user-select:none}
.sm-title{background:none;border:0;padding:0;text-align:left;font-size:21px;font-weight:650;letter-spacing:-0.01em;line-height:1.2}
.sm-dot{display:inline-block;width:10px;height:10px;border-radius:50%;border:1.5px solid var(--turf);flex:none}
.sm-dot.s{background:var(--turf)}
.sm-dot.w{background:linear-gradient(90deg,var(--turf) 50%,transparent 50%)}
.sm-mini{position:relative}
.sm-mb{position:absolute;display:flex;align-items:flex-start;gap:5px;padding:5px 6px;background:var(--bg);
  border:1px solid var(--soft);border-radius:3px;font-size:11px;font-weight:600;line-height:1.2;text-align:left;overflow:visible}
.sm-mb:hover{border-color:var(--ink);background:var(--card)}
.sm-mb.deep{box-shadow:3px 3px 0 var(--tint)}
.sm-mb .sm-dot{width:7px;height:7px;border-width:1px;margin-top:3px}
.sm-fields{display:grid;gap:6px}
.sm-fields .big{font-size:17px;font-weight:650}
.sm-row{display:flex;gap:6px;flex-wrap:wrap;align-items:center}
.sm-grip{position:absolute;top:0;right:-7px;width:14px;height:100%;cursor:ew-resize;touch-action:none}
.sm-grip::after{content:"";position:absolute;top:50%;left:4px;width:6px;height:36px;margin-top:-18px;border-radius:3px;background:var(--ink)}
.sm-grip-v{position:absolute;left:0;bottom:-7px;height:14px;width:100%;cursor:ns-resize;touch-action:none}
.sm-grip-v::after{content:"";position:absolute;left:50%;top:4px;height:6px;width:36px;margin-left:-18px;border-radius:3px;background:var(--ink)}
.sm-wires{position:absolute;left:0;top:0;pointer-events:none;overflow:visible}
.sm-wire{stroke:var(--pass);stroke-width:2;fill:none}
.sm-wire.thin{stroke-width:1.25}
.sm-wire.cross{stroke-dasharray:6 5}
.sm-wire.enum{stroke-width:5;stroke-linecap:round;stroke-dasharray:0.1 13}
.sm-wire.enum-mini{stroke-width:3;stroke-linecap:round;stroke-dasharray:0.1 7}
.sm-head{fill:var(--pass)}
.sm-wlabel{font-size:13px;font-weight:600;fill:var(--ink);paint-order:stroke;stroke:var(--bg);stroke-width:6px;stroke-linejoin:round}
.sm-empty{padding:28px;font-size:15px;color:var(--soft)}
.sm-legend{display:flex;gap:18px;flex-wrap:wrap;margin-top:20px;font-size:13px;color:var(--soft)}
.sm-legend span{display:inline-flex;align-items:center;gap:6px}
.sm-help{margin-top:8px;font-size:13px;color:var(--soft)}
.sm-panel{background:var(--card);border:1.5px solid var(--ink);border-radius:6px;padding:16px;margin-top:18px}
.sm.editing{padding-right:calc(30% + 44px)}
.sm-side{position:fixed;top:0;right:0;width:30%;height:100vh;overflow-y:auto;
  padding:20px;background:var(--bg);border-left:1.5px solid var(--ink);display:flex;flex-direction:column;gap:16px;z-index:5}
.sm-side .sm-panel{margin-top:0}
@media (max-width:760px){
  .sm.editing{padding-right:28px}
  .sm-side{position:static;width:auto;height:auto;overflow:visible;padding:0;border-left:0;margin-top:18px}
}
.sm-panel h2{font-size:17px;font-weight:650;margin-bottom:10px}
.sm-arrow{display:flex;gap:6px;flex-wrap:wrap;align-items:center;padding:8px 0;border-top:1px solid var(--tint);font-size:14px}
.sm-json{width:100%;height:260px;font-family:ui-monospace,Menlo,Consolas,monospace;font-size:12px;line-height:1.5}
.sm-err{color:#F3756A;font-size:14px}
`;

/* Estimated full-size height of a card, used only to draw previews. */
function estHeight(n) {
  let h = CARD_PAD + 26 + (n.sub ? 20 : 0) + (n.status ? 26 : 0);
  if ((n.children || []).length) h += 12 + previewBox(n, n.w - CARD_PAD).h;
  return h;
}
function previewBox(n, avail) {
  const kids = n.children;
  const ox = Math.min(...kids.map((k) => k.x)), oy = Math.min(...kids.map((k) => k.y));
  const bw = Math.max(...kids.map((k) => k.x + k.w)) - ox;
  const bh = Math.max(...kids.map((k) => k.y + estHeight(k))) - oy;
  const s = Math.min(1, Math.max(avail, 60) / bw);
  return { ox, oy, s, w: bw * s, h: bh * s };
}

function routeLinks(kids, links, heightOf, minD, pad) {
  const box = {};
  kids.forEach((k) => { box[k.id] = { x: k.x, y: k.y, w: k.w, h: heightOf(k) }; });
  const vert = (s) => s === "n" || s === "s";
  const groups = {};
  const add = (key, v) => { if (!groups[key]) groups[key] = []; groups[key].push(v); };
  links.forEach((l, i) => {
    const a = box[l.from], b = box[l.to];
    if (!a || !b) return;
    add(l.from + "|" + l.fromSide, { i, end: "a", o: vert(l.fromSide) ? b.x + b.w / 2 : b.y + b.h / 2 });
    add(l.to + "|" + l.toSide, { i, end: "b", o: vert(l.toSide) ? a.x + a.w / 2 : a.y + a.h / 2 });
  });
  const pt = {};
  Object.keys(groups).forEach((key) => {
    const cut = key.lastIndexOf("|");
    const b = box[key.slice(0, cut)], side = key.slice(cut + 1);
    const g = groups[key].sort((p, q) => p.o - q.o || p.i - q.i);
    g.forEach((e, j) => {
      const f = (j + 1) / (g.length + 1);
      pt[e.i + e.end] =
        side === "n" ? { x: b.x + b.w * f, y: b.y } :
        side === "s" ? { x: b.x + b.w * f, y: b.y + b.h } :
        side === "w" ? { x: b.x, y: b.y + b.h * f } :
                       { x: b.x + b.w, y: b.y + b.h * f };
    });
  });
  return links.map((l, i) => {
    const A0 = pt[i + "a"], Z0 = pt[i + "b"];
    if (!A0 || !Z0) return null;
    const na = NORMAL[l.fromSide] || NORMAL.e, nz = NORMAL[l.toSide] || NORMAL.w;
    // Optional gap so a connector starts a little away from each block edge.
    const A = pad ? { x: A0.x + na[0] * pad, y: A0.y + na[1] * pad } : A0;
    const Z = pad ? { x: Z0.x + nz[0] * pad, y: Z0.y + nz[1] * pad } : Z0;
    const d = Math.min(160, Math.max(minD || 30, Math.hypot(Z.x - A.x, Z.y - A.y) * 0.45));
    const c1 = { x: A.x + na[0] * d, y: A.y + na[1] * d };
    const c2 = { x: Z.x + nz[0] * d, y: Z.y + nz[1] * d };
    return {
      d: `M${A.x} ${A.y}C${c1.x} ${c1.y} ${c2.x} ${c2.y} ${Z.x} ${Z.y}`,
      lx: (A.x + 3 * c1.x + 3 * c2.x + Z.x) / 8,
      ly: (A.y + 3 * c1.y + 3 * c2.y + Z.y) / 8,
    };
  });
}

/* Small, scaled-down version of a block's own map, drawn inside its card. */
function Preview({ node, avail, inert, onPick, basePath, registerChip }) {
  const p = previewBox(node, avail);
  const mk = node.children.map((k) => ({
    ...k, x: (k.x - p.ox) * p.s, y: (k.y - p.oy) * p.s, w: k.w * p.s, mh: estHeight(k) * p.s,
  }));
  const wires = routeLinks(mk, node.links || [], (k) => k.mh, 10);
  const enumWires = routeLinks(mk, node.enums || [], (k) => k.mh, 10, 6);
  return (
    <div className="sm-mini" style={{ height: p.h, pointerEvents: inert ? "none" : undefined }}>
      {mk.map((k) => (
        <button
          key={k.id}
          ref={registerChip ? (el) => registerChip(basePath + "/" + k.id, el) : undefined}
          className={"sm-mb" + ((k.children || []).length ? " deep" : "")}
          style={{ left: k.x, top: k.y, width: k.w, minHeight: k.mh }}
          title={k.name}
          aria-label={k.name}
          tabIndex={inert ? -1 : 0}
          onClick={(e) => { e.stopPropagation(); onPick(k.id); }}
        >
          {k.status && <i className={"sm-dot " + k.status} aria-hidden="true" />}
          {k.w >= 64 && <span>{k.name}</span>}
        </button>
      ))}
      <svg className="sm-wires" width={p.w} height={p.h} aria-hidden="true">
        {enumWires.map((w, i) => w && <path key={"e" + i} className="sm-wire enum-mini" d={w.d} />)}
        {wires.map((w, i) => w && <path key={i} className="sm-wire thin" d={w.d} markerEnd="url(#sm-ah)" />)}
      </svg>
    </div>
  );
}

/* Editing is unlocked only when the URL carries ?edit=1. Read it from the
   query string, and also from the hash so it keeps working if the app is ever
   switched to hash routing (#/path?edit=1). */
function editUnlocked() {
  if (typeof window === "undefined") return false;
  const has = (qs) => new URLSearchParams(qs).get("edit") === "1";
  if (has(window.location.search)) return true;
  const h = window.location.hash;
  const q = h.indexOf("?");
  return q >= 0 && has(h.slice(q + 1));
}

export default function StackMap() {
  const [tree, setTree] = useState(SEED);
  const [path, setPath] = useState([]);
  const [edit, setEdit] = useState(false);
  const [hts, setHts] = useState({});
  const [cw, setCw] = useState(0);
  const [json, setJson] = useState(null);
  const [jsonErr, setJsonErr] = useState("");
  const [copied, setCopied] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [editable] = useState(editUnlocked);
  const [ch, setCh] = useState(0);
  const [view, setView] = useState({ zoom: 1, tx: 0, ty: 0 });
  const [chipPos, setChipPos] = useState({});
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const refs = useRef({});
  const chipRefs = useRef({});
  const drag = useRef(null);
  const fileRef = useRef(null);
  const gesture = useRef({ pts: new Map(), dist: 0, mid: null, moved: false, cap: false });
  const fitted = useRef(null);
  const registerChip = (id, el) => { if (el) chipRefs.current[id] = el; else delete chipRefs.current[id]; };

  const trail = [tree];
  for (const id of path) {
    const next = (trail[trail.length - 1].children || []).find((c) => c.id === id);
    if (!next) break;
    trail.push(next);
  }
  const cur = trail.slice(1).map((n) => n.id);
  const node = trail[trail.length - 1];
  const kids = node.children || [];
  const links = node.links || [];
  const enums = node.enums || []; /* headless dotted connectors between two blocks on this level */
  const crossLinks = tree.crossLinks || []; /* arrows between blocks in different parents */

  const change = (fn) => {
    setJson(null);
    setDirty(true);
    setTree((prev) => {
      const t = JSON.parse(JSON.stringify(prev));
      let n = t;
      for (const id of cur) {
        const c = (n.children || []).find((k) => k.id === id);
        if (!c) break;
        n = c;
      }
      if (!n.children) n.children = [];
      if (!n.links) n.links = [];
      if (!n.enums) n.enums = [];
      fn(n);
      return t;
    });
  };
  const changeRoot = (fn) => {
    setJson(null);
    setDirty(true);
    setTree((prev) => {
      const t = JSON.parse(JSON.stringify(prev));
      if (!t.crossLinks) t.crossLinks = [];
      fn(t);
      return t;
    });
  };

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const read = () => { setCw(el.clientWidth); setCh(el.clientHeight); };
    read();
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Re-measure block heights after every render so previews and wires line up.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    const next = {};
    let diff = false;
    kids.forEach((k) => {
      const el = refs.current[k.id];
      if (!el) return;
      next[k.id] = el.offsetHeight;
      if (hts[k.id] !== next[k.id]) diff = true;
    });
    if (diff) setHts((h) => ({ ...h, ...next }));
  });

  // Measure preview-chip centres in canvas content coordinates (zoom-invariant)
  // so the root overview can draw cross-level arrows chip-to-chip.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    const ids = Object.keys(chipRefs.current);
    if (!canvas || !ids.length || !crossLinks.length) {
      if (Object.keys(chipPos).length) setChipPos({});
      return;
    }
    const crect = canvas.getBoundingClientRect();
    const z = view.zoom || 1;
    const next = {};
    let diff = ids.length !== Object.keys(chipPos).length;
    ids.forEach((id) => {
      const el = chipRefs.current[id];
      if (!el) return;
      const r = el.getBoundingClientRect();
      const x = (r.left + r.width / 2 - crect.left) / z;
      const y = (r.top + r.height / 2 - crect.top) / z;
      next[id] = { x, y, hw: r.width / (2 * z), hh: r.height / (2 * z) };
      const p = chipPos[id];
      if (!p || Math.abs(p.x - x) > 0.5 || Math.abs(p.y - y) > 0.5) diff = true;
    });
    if (diff) setChipPos(next);
  });

  const heightOf = (k) => k.h || hts[k.id] || 110;
  const contentW = Math.max(640, ...kids.map((k) => k.x + k.w + 30)) + (edit ? 200 : 0);
  const contentH = Math.max(140, ...kids.map((k) => k.y + heightOf(k) + 30)) + (edit ? 200 : 0);
  const wires = routeLinks(kids, links, heightOf);
  const enumWires = routeLinks(kids, enums, heightOf, undefined, 12); /* headless dotted "and more" connectors, inset from the blocks */

  /* Where a cross-level endpoint lands in the current canvas. We only ever show
     the current level and one level deep, so an endpoint is clamped to its
     shallowest visible ancestor: a visible block if it's a direct child, else
     the one-level-deep preview chip of whichever visible block contains it
     (anything deeper collapses onto that same chip). null = not on screen.
     `key` identifies the slot so several deep endpoints on one chip fan out. */
  const crossSlot = (pathStr) => {
    const r = resolvePath(tree, pathStr);
    if (!r) return null;
    const ids = r.ids;
    if (cur.some((c, i) => ids[i] !== c)) return null; // endpoint isn't under the current node
    const rel = ids.slice(cur.length);
    if (rel.length === 0) return null;
    if (rel.length === 1) {
      const b = kids.find((k) => k.id === rel[0]);
      return b ? { key: [...cur, rel[0]].join("/"), x: b.x, y: b.y, w: b.w, h: heightOf(b) } : null;
    }
    if (!kids.some((k) => k.id === rel[0])) return null;
    const chipPath = [...cur, rel[0], rel[1]].join("/"); // clamp to the 1-deep chip
    const cp = chipPos[chipPath];
    return cp ? { key: chipPath, x: cp.x - cp.hw, y: cp.y - cp.hh, w: cp.hw * 2, h: cp.hh * 2 } : null;
  };
  /* Even spread of touch points along each side, the same idea as same-level
     links: group every cross endpoint by its slot+side, then place them at
     fractions (j+1)/(n+1) across that side so they fan out instead of stacking. */
  const reqs = [];
  crossLinks.forEach((l, i) => {
    const sf = crossSlot(l.from); if (sf) reqs.push({ i, end: "from", key: sf.key, side: l.fromSide });
    const st = crossSlot(l.to); if (st) reqs.push({ i, end: "to", key: st.key, side: l.toSide });
  });
  const slotGroups = {};
  reqs.forEach((rq) => {
    const key = rq.key + "|" + rq.side;
    if (!slotGroups[key]) slotGroups[key] = [];
    slotGroups[key].push(rq);
  });
  const fracOf = {};
  Object.keys(slotGroups).forEach((key) => {
    const list = slotGroups[key].sort((p, q) => p.i - q.i || (p.end < q.end ? -1 : 1));
    list.forEach((rq, j) => { fracOf[rq.i + ":" + rq.end] = (j + 1) / (list.length + 1); });
  });
  const anchorFor = (l, i, end) => {
    const path = end === "from" ? l.from : l.to;
    const side = end === "from" ? l.fromSide : l.toSide;
    const s = crossSlot(path);
    if (!s) return null;
    const f = fracOf[i + ":" + end] != null ? fracOf[i + ":" + end] : 0.5;
    const n = NORMAL[side] || NORMAL.e;
    const x = side === "w" ? s.x : side === "e" ? s.x + s.w : s.x + s.w * f;
    const y = side === "n" ? s.y : side === "s" ? s.y + s.h : s.y + s.h * f;
    return { x, y, nx: n[0], ny: n[1] };
  };
  const crossWires = crossLinks.map((l, i) => {
    const a = anchorFor(l, i, "from");
    const b = anchorFor(l, i, "to");
    if (a && b) { const g = bez(a, b); return { d: g.d, lx: g.lx, ly: g.ly, label: l.label }; }
    const stub = Math.max(56, (l.label ? l.label.length : 0) * 13); /* ~2x the label width so it stays readable */
    if (a) { const o = { x: a.x + a.nx * stub, y: a.y + a.ny * stub }; return { d: `M${a.x} ${a.y}L${o.x} ${o.y}`, lx: (a.x + o.x) / 2, ly: (a.y + o.y) / 2, label: l.label }; }
    if (b) { const o = { x: b.x + b.nx * stub, y: b.y + b.ny * stub }; return { d: `M${o.x} ${o.y}L${b.x} ${b.y}`, lx: (b.x + o.x) / 2, ly: (b.y + o.y) / 2, label: l.label }; }
    return null;
  });

  /* Collect every arrow label and nudge overlapping ones down a little so both
     stay legible (approximate is fine — the exact wire is still drawn). */
  const labelItems = [];
  wires.forEach((w, i) => { if (w && links[i].label) labelItems.push({ key: "w" + i, x: w.lx, y: w.ly + 4, text: links[i].label }); });
  crossWires.forEach((w, i) => { if (w && w.label) labelItems.push({ key: "x" + i, x: w.lx, y: w.ly + 4, text: w.label }); });
  const placedLabels = [];
  labelItems.forEach((it) => {
    let guard = 0;
    while (guard++ < 40 && placedLabels.some((p) => Math.abs(p.x - it.x) < 72 && Math.abs(p.y - it.y) < 14)) it.y += 15;
    placedLabels.push({ x: it.x, y: it.y });
  });

  /* View mode shows the whole map scaled to fit, centred. Ctrl+wheel (or
     trackpad/pinch) zooms, and dragging pans; see the gesture handlers below.
     Re-fit on each new level until the viewer first zooms or pans it. */
  const levelKey = cur.join("/");
  useLayoutEffect(() => {
    if (!cw || !ch) return;
    const g = gesture.current;
    if (fitted.current !== levelKey) { fitted.current = levelKey; g.userTouched = false; }
    if (g.userTouched) return;
    const z = Math.max(ZMIN, Math.min(1, (cw - 48) / contentW, (ch - 48) / contentH));
    setView({ zoom: z, tx: Math.max(16, (cw - contentW * z) / 2), ty: Math.max(16, (ch - contentH * z) / 2) });
  }, [edit, cw, ch, contentW, contentH, levelKey]);

  const snap = (v) => Math.round(v / 10) * 10;
  const onDown = (e, k, mode) => {
    if (!edit) return;
    if (mode === "move" && e.target.closest("input,button,select,textarea,.sm-grip,.sm-grip-v")) return;
    gesture.current.userTouched = true; /* editing a block shouldn't re-fit the view */
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { id: k.id, mode, sx: e.clientX, sy: e.clientY, x: k.x, y: k.y, w: k.w, h: heightOf(k) };
  };
  const onMove = (e) => {
    const g = drag.current;
    if (!g) return;
    const z = view.zoom || 1; /* pointer moves in screen px; blocks live in content px */
    const dx = (e.clientX - g.sx) / z, dy = (e.clientY - g.sy) / z;
    change((n) => {
      const c = n.children.find((q) => q.id === g.id);
      if (!c) return;
      if (g.mode === "move") {
        c.x = Math.max(0, snap(g.x + dx));
        c.y = Math.max(0, snap(g.y + dy));
      } else if (g.mode === "resizeV") {
        c.h = Math.max(60, snap(g.h + dy));
      } else {
        c.w = Math.max(160, snap(g.w + dx));
      }
    });
  };
  const onUp = () => { drag.current = null; };

  /* Pan/zoom the whole map. One pointer pans; two pinch-zoom. A drag past a
     few pixels is swallowed on click so it never opens a block. In edit mode
     we only pan from empty space, so dragging a block still moves the block. */
  const panDown = (e) => {
    if (edit && e.target.closest(".sm-block")) return;
    const g = gesture.current;
    g.pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
    g.moved = false;
    if (g.pts.size === 2) {
      const p = [...g.pts.values()];
      g.dist = Math.hypot(p[0].x - p[1].x, p[0].y - p[1].y);
      g.mid = { x: (p[0].x + p[1].x) / 2, y: (p[0].y + p[1].y) / 2 };
      g.userTouched = true;
      try { wrapRef.current.setPointerCapture(e.pointerId); } catch (_) {}
    }
  };
  const panMove = (e) => {
    const g = gesture.current;
    if (!g.pts.has(e.pointerId)) return;
    const prev = g.pts.get(e.pointerId);
    g.pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const r = wrapRef.current.getBoundingClientRect();
    if (g.pts.size === 1) {
      const dx = e.clientX - prev.x, dy = e.clientY - prev.y;
      if (!g.moved && Math.abs(dx) + Math.abs(dy) > 3) {
        g.moved = true; g.userTouched = true;
        try { wrapRef.current.setPointerCapture(e.pointerId); } catch (_) {}
      }
      if (g.moved) setView((v) => ({ ...v, tx: v.tx + dx, ty: v.ty + dy }));
    } else if (g.pts.size >= 2) {
      g.moved = true;
      const p = [...g.pts.values()];
      const dist = Math.hypot(p[0].x - p[1].x, p[0].y - p[1].y);
      const mid = { x: (p[0].x + p[1].x) / 2, y: (p[0].y + p[1].y) / 2 };
      const px = mid.x - r.left, py = mid.y - r.top;
      const ratio = g.dist ? dist / g.dist : 1;
      const mdx = g.mid ? mid.x - g.mid.x : 0, mdy = g.mid ? mid.y - g.mid.y : 0;
      setView((v) => {
        const nz = clampZ(v.zoom * ratio);
        const k = nz / v.zoom;
        const bx = v.tx + mdx, by = v.ty + mdy;
        return { zoom: nz, tx: px - (px - bx) * k, ty: py - (py - by) * k };
      });
      g.dist = dist; g.mid = mid;
    }
  };
  const panUp = (e) => {
    const g = gesture.current;
    g.pts.delete(e.pointerId);
    try { wrapRef.current.releasePointerCapture(e.pointerId); } catch (_) {}
    if (g.pts.size < 2) { g.dist = 0; g.mid = null; }
  };
  const panClickCapture = (e) => {
    if (gesture.current.moved) { e.stopPropagation(); e.preventDefault(); gesture.current.moved = false; }
  };

  /* Ctrl+wheel zooms toward the cursor. Bound natively so preventDefault works
     (React's onWheel is passive), and only while in view mode. */
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      if (!e.ctrlKey) return;
      e.preventDefault();
      const r = el.getBoundingClientRect();
      const px = e.clientX - r.left, py = e.clientY - r.top;
      gesture.current.userTouched = true;
      setView((v) => {
        const nz = clampZ(v.zoom * Math.exp(-e.deltaY * 0.0015));
        const k = nz / v.zoom;
        return { zoom: nz, tx: px - (px - v.tx) * k, ty: py - (py - v.ty) * k };
      });
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [edit]);

  const setField = (id, key, val) => change((n) => { const c = n.children.find((q) => q.id === id); if (c) c[key] = val; });
  const addBlock = () => {
    const id = "b" + Date.now().toString(36);
    const y = kids.length ? Math.max(...kids.map((k) => k.y + heightOf(k))) + 30 : 20;
    change((n) => { n.children.push({ id, name: "New block", sub: "", x: 20, y, w: 240, info: {} }); });
  };
  const removeBlock = (id) => change((n) => {
    n.children = n.children.filter((c) => c.id !== id);
    n.links = n.links.filter((l) => l.from !== id && l.to !== id);
  });
  const addArrow = () => change((n) => {
    if (n.children.length < 2) return;
    n.links.push({ from: n.children[0].id, to: n.children[1].id, fromSide: "e", toSide: "w", label: "" });
  });
  const setArrow = (i, key, val) => change((n) => { if (n.links[i]) n.links[i][key] = val; });
  const removeArrow = (i) => change((n) => { n.links.splice(i, 1); });

  const addEnum = () => change((n) => {
    if (n.children.length < 2) return;
    n.enums.push({ from: n.children[0].id, to: n.children[1].id, fromSide: "e", toSide: "w" });
  });
  const setEnum = (i, key, val) => change((n) => { if (n.enums[i]) n.enums[i][key] = val; });
  const removeEnum = (i) => change((n) => { n.enums.splice(i, 1); });

  const addCross = () => {
    const opts = flattenBlocks(tree);
    if (!opts.length) return;
    changeRoot((t) => { t.crossLinks.push({ from: opts[0].path, to: (opts[1] || opts[0]).path, fromSide: "e", toSide: "w", label: "" }); });
  };
  const setCross = (i, key, val) => changeRoot((t) => { if (t.crossLinks[i]) t.crossLinks[i][key] = val; });
  const removeCross = (i) => changeRoot((t) => { t.crossLinks.splice(i, 1); });

  const showJson = () => { setJson(JSON.stringify(tree, null, 2)); setJsonErr(""); setCopied(false); };
  const applyJson = () => {
    try {
      const t = JSON.parse(json);
      if (!t || typeof t !== "object" || Array.isArray(t)) throw new Error("bad shape");
      setTree(t); setPath([]); setJsonErr(""); setDirty(true);
    } catch (err) {
      setJsonErr("That JSON didn't parse. Check for a missing comma or bracket, then apply again.");
    }
  };
  const copyJson = () => {
    try {
      navigator.clipboard.writeText(json).then(() => setCopied(true), () => setCopied(false));
    } catch (err) {
      setCopied(false);
    }
  };

  /* Save the live map (current state at this click, including any drags made
     since the JSON panel was opened) as a drop-in src/data/stack.json. */
  const downloadJson = () => {
    const text = JSON.stringify(tree, null, 2) + "\n";
    const url = URL.createObjectURL(new Blob([text], { type: "application/json" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "stack.json";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    setDirty(false);
  };

  /* Preview a friend's file before committing it. Reject anything that isn't
     an object with a children array, leaving the current map untouched. */
  const loadFile = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = ""; // allow re-picking the same file later
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const t = JSON.parse(reader.result);
        if (!t || typeof t !== "object" || Array.isArray(t) || !Array.isArray(t.children)) throw new Error("bad shape");
        setTree(t); setPath([]); setJson(null); setJsonErr(""); setCopied(false); setDirty(false);
      } catch (err) {
        setJsonErr("That file isn't a valid map. It must be JSON with a top-level \"children\" array.");
      }
    };
    reader.onerror = () => setJsonErr("That file couldn't be read. Try choosing it again.");
    reader.readAsText(file);
  };

  /* Warn before leaving with changes that haven't been downloaded yet. */
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const info = Object.entries(node.info || {});

  return (
    <div className={"sm" + (edit ? " editing" : "")}>
      <style>{CSS}</style>

      <div className="sm-top">
        <nav className="sm-crumbs" aria-label="Breadcrumb">
          {trail.map((n, i) =>
            i < trail.length - 1 ? (
              <React.Fragment key={i}>
                <button className="sm-link" onClick={() => setPath(cur.slice(0, i))}>{n.name}</button>
                <span aria-hidden="true">/</span>
              </React.Fragment>
            ) : (
              <span key={i} className="sm-here">{n.name}</span>
            )
          )}
        </nav>
        {editable && (
          <div className="sm-bar">
            {edit && <button className="sm-btn" onClick={addBlock}>Add block</button>}
            {edit && <button className="sm-btn" onClick={addArrow}>Add arrow</button>}
            {edit && <button className="sm-btn" onClick={downloadJson}>Download JSON</button>}
            {edit && <button className="sm-btn" onClick={() => fileRef.current && fileRef.current.click()}>Load JSON file</button>}
            {edit && <button className="sm-btn" onClick={json === null ? showJson : () => setJson(null)}>{json === null ? "Show JSON" : "Hide JSON"}</button>}
            {edit && <input ref={fileRef} type="file" accept=".json,application/json" onChange={loadFile} style={{ display: "none" }} />}
            <button className={"sm-btn" + (edit ? " on" : "")} aria-pressed={edit} onClick={() => { setEdit(!edit); setJson(null); }}>
              {edit ? "Done editing" : "Edit layout"}
            </button>
          </div>
        )}
      </div>

      {edit && (
        <div className="sm-panel" style={{ marginTop: 0, marginBottom: 18, padding: "10px 14px" }}>
          <p className="sm-sub">Changes aren't saved to the site. Download the JSON and send it to Anthony to publish.</p>
          {jsonErr && json === null && <p className="sm-err" style={{ marginTop: 6 }}>{jsonErr}</p>}
        </div>
      )}

      {cur.length > 0 && (
        <header>
          <h2 className="sm-h">{node.name}</h2>
          <p className="sm-sub">{node.sub}</p>
          {node.status && <p className="sm-status" style={{ marginTop: 6 }}><i className={"sm-dot " + node.status} aria-hidden="true" />{STATUS[node.status]}</p>}
          {info.length > 0 && (
            <dl className="sm-facts">
              {info.map(([k, v]) => (
                <div key={k}>
                  <dt>{k}</dt>
                  <dd>{Array.isArray(v) ? <ul>{v.map((t, i) => <li key={i}>{t}</li>)}</ul> : v}</dd>
                </div>
              ))}
            </dl>
          )}
        </header>
      )}

      <div
        ref={wrapRef}
        className={"sm-stage" + (edit ? " edit" : " pan")}
        style={kids.length || edit
          ? { flex: "1 1 auto", minHeight: 320, overflow: "hidden", touchAction: "none" }
          : { height: 0 }}
        onPointerDown={panDown}
        onPointerMove={panMove}
        onPointerUp={panUp}
        onPointerCancel={panUp}
        onClickCapture={panClickCapture}
      >
        {edit && kids.length === 0 && <p className="sm-empty">This level is empty. Add a block to start mapping what's inside {node.name}.</p>}
        <div ref={canvasRef} className="sm-canvas" style={{ width: contentW, height: contentH, transform: `translate(${view.tx}px, ${view.ty}px) scale(${view.zoom})` }}>
          {kids.map((k) => (
            <div
              key={k.id}
              ref={(el) => { refs.current[k.id] = el; }}
              className={"sm-block" + (edit ? " edit" : "")}
              style={{ left: k.x, top: k.y, width: k.w, ...(k.h ? { height: k.h, overflow: "hidden" } : null) }}
              onClick={edit ? undefined : () => setPath([...cur, k.id])}
              onPointerDown={(e) => onDown(e, k, "move")}
              onPointerMove={onMove}
              onPointerUp={onUp}
              onPointerCancel={onUp}
            >
              {edit ? (
                <div className="sm-fields">
                  <input className="big" aria-label="Block name" value={k.name} onChange={(e) => setField(k.id, "name", e.target.value)} />
                  <input aria-label="Short description" placeholder="What it does, in a few words" value={k.sub || ""} onChange={(e) => setField(k.id, "sub", e.target.value)} />
                  <div className="sm-row">
                    <select aria-label="Status" value={k.status || ""} onChange={(e) => setField(k.id, "status", e.target.value)}>
                      <option value="">No status</option>
                      {Object.keys(STATUS).map((s) => <option key={s} value={s}>{STATUS[s]}</option>)}
                    </select>
                    <button className="sm-btn quiet" onClick={() => setPath([...cur, k.id])}>Open</button>
                    <button className="sm-btn quiet" onClick={() => removeBlock(k.id)}>Delete</button>
                  </div>
                </div>
              ) : (
                <div>
                  <button className="sm-title" onClick={(e) => { e.stopPropagation(); setPath([...cur, k.id]); }}>{k.name}</button>
                  {k.sub && <p className="sm-sub">{k.sub}</p>}
                  {k.status && <p className="sm-status" style={{ marginTop: 6 }}><i className={"sm-dot " + k.status} aria-hidden="true" />{STATUS[k.status]}</p>}
                </div>
              )}
              {(k.children || []).length > 0 && (
                <Preview node={k} avail={k.w - CARD_PAD} inert={edit} onPick={(childId) => setPath([...cur, k.id, childId])} basePath={[...cur, k.id].join("/")} registerChip={registerChip} />
              )}
              {edit && <span className="sm-grip" role="separator" aria-label="Drag to resize width" onPointerDown={(e) => onDown(e, k, "resize")} />}
              {edit && <span className="sm-grip-v" role="separator" aria-label="Drag to resize height" onPointerDown={(e) => onDown(e, k, "resizeV")} />}
            </div>
          ))}

          <svg className="sm-wires" width={contentW} height={contentH} aria-hidden="true">
            <defs>
              <marker id="sm-ah" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto">
                <path className="sm-head" d="M0 0L10 5L0 10z" />
              </marker>
            </defs>
            {edit && (
              <g className="sm-origin">
                <line x1="0" y1="0" x2="72" y2="0" />
                <line x1="0" y1="0" x2="0" y2="72" />
                <circle cx="0" cy="0" r="5" />
                <text x="10" y="20">origin (0, 0) — blocks can't go above or left of here</text>
              </g>
            )}
            {enumWires.map((w, i) => w && <path key={"e" + i} className="sm-wire enum" d={w.d} />)}
            {wires.map((w, i) => w && <path key={i} className="sm-wire" d={w.d} markerEnd="url(#sm-ah)" />)}
            {crossWires.map((w, i) => w && <path key={"x" + i} className="sm-wire cross" d={w.d} markerEnd="url(#sm-ah)" />)}
            {labelItems.map((it) => (
              <text key={it.key} className="sm-wlabel" x={it.x} y={it.y} textAnchor="middle">{it.text}</text>
            ))}
          </svg>
        </div>
      </div>

      {kids.length > 0 && !edit && (
        <>
          <div className="sm-legend">
            {Object.keys(STATUS).map((s) => <span key={s}><i className={"sm-dot " + s} aria-hidden="true" />{STATUS[s]}</span>)}
          </div>
          <p className="sm-help">Zoom: Ctrl + scroll (pinch on touch) · Pan: drag (one finger on touch)</p>
        </>
      )}

      {edit && (
        <aside className="sm-side">
          {links.length > 0 && (
            <section className="sm-panel">
              <h2>Arrows on this level</h2>
              {links.map((l, i) => (
                <div key={i} className="sm-arrow">
                  <select aria-label="From block" value={l.from} onChange={(e) => setArrow(i, "from", e.target.value)}>
                    {kids.map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}
                  </select>
                  <select aria-label="From side" value={l.fromSide} onChange={(e) => setArrow(i, "fromSide", e.target.value)}>
                    {Object.keys(SIDES).map((s) => <option key={s} value={s}>{SIDES[s]}</option>)}
                  </select>
                  <span>to</span>
                  <select aria-label="To block" value={l.to} onChange={(e) => setArrow(i, "to", e.target.value)}>
                    {kids.map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}
                  </select>
                  <select aria-label="To side" value={l.toSide} onChange={(e) => setArrow(i, "toSide", e.target.value)}>
                    {Object.keys(SIDES).map((s) => <option key={s} value={s}>{SIDES[s]}</option>)}
                  </select>
                  <input aria-label="Arrow label" placeholder="Wheel setpoints" value={l.label || ""} onChange={(e) => setArrow(i, "label", e.target.value)} style={{ flex: "1 1 140px" }} />
                  <button className="sm-btn quiet" onClick={() => removeArrow(i)}>Remove</button>
                </div>
              ))}
            </section>
          )}

          <section className="sm-panel">
            <h2>Enumerate arrows</h2>
            <p className="sm-sub" style={{ marginBottom: 10 }}>A headless dotted connector between two blocks — use it to imply repeated items (e.g. more motors) without drawing each one.</p>
            {enums.map((l, i) => (
              <div key={i} className="sm-arrow">
                <select aria-label="From block" value={l.from} onChange={(e) => setEnum(i, "from", e.target.value)}>
                  {kids.map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}
                </select>
                <select aria-label="From side" value={l.fromSide} onChange={(e) => setEnum(i, "fromSide", e.target.value)}>
                  {Object.keys(SIDES).map((s) => <option key={s} value={s}>{SIDES[s]}</option>)}
                </select>
                <span>to</span>
                <select aria-label="To block" value={l.to} onChange={(e) => setEnum(i, "to", e.target.value)}>
                  {kids.map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}
                </select>
                <select aria-label="To side" value={l.toSide} onChange={(e) => setEnum(i, "toSide", e.target.value)}>
                  {Object.keys(SIDES).map((s) => <option key={s} value={s}>{SIDES[s]}</option>)}
                </select>
                <button className="sm-btn quiet" onClick={() => removeEnum(i)}>Remove</button>
              </div>
            ))}
            <div className="sm-bar" style={{ marginTop: 10 }}>
              <button className="sm-btn" onClick={addEnum}>Add enumerate</button>
            </div>
          </section>

          <section className="sm-panel">
            <h2>Cross-level arrows</h2>
            <p className="sm-sub" style={{ marginBottom: 10 }}>Connect any block to any block, even in different parents. Drawn chip-to-chip on the overview, and as a short labelled stub when you open either side.</p>
            {crossLinks.map((l, i) => (
              <div key={i} className="sm-arrow">
                <select aria-label="From block" value={l.from} onChange={(e) => setCross(i, "from", e.target.value)}>
                  {flattenBlocks(tree).map((o) => <option key={o.path} value={o.path}>{o.label}</option>)}
                </select>
                <select aria-label="From side" value={l.fromSide} onChange={(e) => setCross(i, "fromSide", e.target.value)}>
                  {Object.keys(SIDES).map((s) => <option key={s} value={s}>{SIDES[s]}</option>)}
                </select>
                <span>to</span>
                <select aria-label="To block" value={l.to} onChange={(e) => setCross(i, "to", e.target.value)}>
                  {flattenBlocks(tree).map((o) => <option key={o.path} value={o.path}>{o.label}</option>)}
                </select>
                <select aria-label="To side" value={l.toSide} onChange={(e) => setCross(i, "toSide", e.target.value)}>
                  {Object.keys(SIDES).map((s) => <option key={s} value={s}>{SIDES[s]}</option>)}
                </select>
                <input aria-label="Cross arrow label" placeholder="Master clock" value={l.label || ""} onChange={(e) => setCross(i, "label", e.target.value)} style={{ flex: "1 1 140px" }} />
                <button className="sm-btn quiet" onClick={() => removeCross(i)}>Remove</button>
              </div>
            ))}
            <div className="sm-bar" style={{ marginTop: 10 }}>
              <button className="sm-btn" onClick={addCross}>Add cross arrow</button>
            </div>
          </section>
        </aside>
      )}

      {edit && json !== null && (
        <section className="sm-panel">
          <h2>Map data</h2>
          <p className="sm-sub" style={{ marginBottom: 10 }}>Copy this into your repo to save the layout. You can also edit details here, such as purpose and starter tasks, then apply.</p>
          <textarea className="sm-json" aria-label="Map data as JSON" spellCheck={false} value={json} onChange={(e) => { setJson(e.target.value); setCopied(false); }} />
          {jsonErr && <p className="sm-err">{jsonErr}</p>}
          <div className="sm-bar" style={{ marginTop: 10 }}>
            <button className="sm-btn" onClick={copyJson}>{copied ? "Copied" : "Copy JSON"}</button>
            <button className="sm-btn" onClick={applyJson}>Apply JSON</button>
          </div>
        </section>
      )}
    </div>
  );
}
